import { KeyLabel } from '../../key-label.model';
import { ReportMetricV2 } from './report-metric-v2.enum';

export enum ReportDimensionV2 {
  // Time Dimensions
  GRANULARITY_YEAR = 'granularity_year',
  GRANULARITY_QUARTER = 'granularity_quarter', 
  GRANULARITY_MONTH = 'granularity_month',
  GRANULARITY_WEEK = 'granularity_week',
  GRANULARITY_DAY = 'granularity_day',
  GRANULARITY_HOUR = 'granularity_hour',
  HOUR = 'hour',

  // Publisher & Supply Dimensions
  PUBLISHER_ID = 'publisher.id',
  PUBLISHER_AD_UNIT_ID = 'publisher.ad_unit_id',
  PUBLISHER_DOMAIN = 'publisher.domain',
  INVENTORY_TYPE = 'inventory_type',
  CREATIVE_SIZE = 'creative_size',
  PAGE_DOMAIN = 'page_domain',
  SUPPLY_SOURCE = 'supply_source',
  IMPRESSION_INSTREAM = 'impression_instream',
  APP_BUNDLE = 'app.bundle',
  DEVICE_TYPE = 'user.agent.device.type',
  VIEWABILITY_STANDARD = 'viewability_standard',
  PB_VERSION = 'source_ext_wrapper_version',
  ENDPOINT = 'endpoint',
  EIDS_SOURCES = 'eids_sources',
  NOBID_REASON = 'nobid_reason',
  US_PRIVACY = 'us_privacy',
  IVT_PROVIDER = 'ivt_provider',
  IVT_TYPE = 'ivt_type',

  // Demand Partner Dimensions
  DSP_ID = 'dsp.id',
  DSP_BUYER_ID = 'dsp.buyer_id',
  DEAL_ID = 'deal.id',
  DP_ID = 'dp.id',
  DSP_CREATIVE_ID = 'dsp.creative_id',

  // Technical Dimensions
  USER_AGENT_OS_NAME = 'user.agent.os.name',
  MEDIA_TYPE = 'publisher.media_type',
  COUNTRY = 'user.geo.country',
  REGION = 'country_region',

  // Predictive Viewability Dimensions
  PROB_DECILE = 'prob_decile',
  GROUPM_VIEW_PROB_DECILE = 'groupm_view_prob_decile',
  VIEW_PROB_DECILE = 'view_prob_decile'
}

export const ReportDimensionV2Labels = {
  // Time Dimensions
  [ReportDimensionV2.GRANULARITY_YEAR]: 'Year',
  [ReportDimensionV2.GRANULARITY_QUARTER]: 'Quarter',
  [ReportDimensionV2.GRANULARITY_MONTH]: 'Month',
  [ReportDimensionV2.GRANULARITY_WEEK]: 'Week',
  [ReportDimensionV2.GRANULARITY_DAY]: 'Day',
  [ReportDimensionV2.GRANULARITY_HOUR]: 'Day & Time',
  [ReportDimensionV2.HOUR]: 'Hour',

  // Publisher & Supply Dimensions
  [ReportDimensionV2.PUBLISHER_ID]: 'Publisher',
  [ReportDimensionV2.PUBLISHER_AD_UNIT_ID]: 'Ad Unit',
  [ReportDimensionV2.PUBLISHER_DOMAIN]: 'Publisher Domain',
  [ReportDimensionV2.INVENTORY_TYPE]: 'Inventory type',
  [ReportDimensionV2.CREATIVE_SIZE]: 'Creative Size',
  [ReportDimensionV2.PAGE_DOMAIN]: 'Landing Page Domain',
  [ReportDimensionV2.SUPPLY_SOURCE]: 'Supply Source',
  [ReportDimensionV2.IMPRESSION_INSTREAM]: 'Impression Instream',
  [ReportDimensionV2.APP_BUNDLE]: 'App Bundle',
  [ReportDimensionV2.DEVICE_TYPE]: 'Device type',
  [ReportDimensionV2.VIEWABILITY_STANDARD]: 'Viewability standard',
  [ReportDimensionV2.PB_VERSION]: 'PB Version',
  [ReportDimensionV2.ENDPOINT]: 'Endpoint',
  [ReportDimensionV2.EIDS_SOURCES]: 'EIDs Sources',
  [ReportDimensionV2.NOBID_REASON]: 'No Bid Reason',
  [ReportDimensionV2.US_PRIVACY]: 'US Privacy',
  [ReportDimensionV2.IVT_PROVIDER]: 'IVT Provider',
  [ReportDimensionV2.IVT_TYPE]: 'IVT Type',

  // Demand Partner Dimensions
  [ReportDimensionV2.DSP_ID]: 'DSP',
  [ReportDimensionV2.DSP_BUYER_ID]: 'Buyer ID',
  [ReportDimensionV2.DEAL_ID]: 'Deal',
  [ReportDimensionV2.DP_ID]: 'Demand Partner',
  [ReportDimensionV2.DSP_CREATIVE_ID]: 'DSP Creative ID',

  // Technical Dimensions
  [ReportDimensionV2.USER_AGENT_OS_NAME]: 'OS',
  [ReportDimensionV2.MEDIA_TYPE]: 'Media type',
  [ReportDimensionV2.COUNTRY]: 'Country',
  [ReportDimensionV2.REGION]: 'Region',

  // Predictive Viewability Dimensions
  [ReportDimensionV2.PROB_DECILE]: 'PV decile',
  [ReportDimensionV2.GROUPM_VIEW_PROB_DECILE]: 'PV decile GroupM',
  [ReportDimensionV2.VIEW_PROB_DECILE]: 'PV decile MRC'
};



export const ReportDimensionV2KL = () =>
  Object.values(ReportDimensionV2).map(
    (key) =>
      new KeyLabel<ReportDimensionV2>(
        key,
        ReportDimensionV2Labels[key] || key // Fallback to key if label is undefined
      )
  ).filter(item => item.label !== undefined); // Filter out any undefined labels

export const ReportDimensionsV2Map = (): {
  [key: string]: KeyLabel<ReportDimensionV2>;
} =>
  ReportDimensionV2KL().reduce((prev, curr) => {
    prev[curr.key] = curr;
    return prev;
  }, {});

// Mapping from dimensions to their corresponding metric enum values
export const DimensionToMetricMap = {
  [ReportDimensionV2.GRANULARITY_YEAR]: ReportMetricV2.DIM_GRANULARITY_YEAR,
  [ReportDimensionV2.GRANULARITY_QUARTER]: ReportMetricV2.DIM_GRANULARITY_QUARTER,
  [ReportDimensionV2.GRANULARITY_MONTH]: ReportMetricV2.DIM_GRANULARITY_MONTH,
  [ReportDimensionV2.GRANULARITY_WEEK]: ReportMetricV2.DIM_GRANULARITY_WEEK,
  [ReportDimensionV2.GRANULARITY_DAY]: ReportMetricV2.DIM_GRANULARITY_DAY,
  [ReportDimensionV2.GRANULARITY_HOUR]: ReportMetricV2.DIM_GRANULARITY_HOUR,
  [ReportDimensionV2.HOUR]: ReportMetricV2.DIM_HOUR,
  [ReportDimensionV2.PUBLISHER_ID]: ReportMetricV2.DIM_PUBLISHER_ID,
  [ReportDimensionV2.PUBLISHER_AD_UNIT_ID]: ReportMetricV2.DIM_PUBLISHER_AD_UNIT_ID,
  [ReportDimensionV2.PUBLISHER_DOMAIN]: ReportMetricV2.DIM_PUBLISHER_DOMAIN,
  [ReportDimensionV2.INVENTORY_TYPE]: ReportMetricV2.DIM_INVENTORY_TYPE,
  [ReportDimensionV2.CREATIVE_SIZE]: ReportMetricV2.DIM_CREATIVE_SIZE,
  [ReportDimensionV2.PAGE_DOMAIN]: ReportMetricV2.DIM_PAGE_DOMAIN,
  [ReportDimensionV2.SUPPLY_SOURCE]: ReportMetricV2.DIM_SUPPLY_SOURCE,
  [ReportDimensionV2.IMPRESSION_INSTREAM]: ReportMetricV2.DIM_IMPRESSION_INSTREAM,
  [ReportDimensionV2.DSP_ID]: ReportMetricV2.DIM_DSP_ID,
  [ReportDimensionV2.DSP_BUYER_ID]: ReportMetricV2.DIM_DSP_BUYER_ID,
  [ReportDimensionV2.DEAL_ID]: ReportMetricV2.DIM_DEAL_ID,
  [ReportDimensionV2.DP_ID]: ReportMetricV2.DIM_DP_ID,
  [ReportDimensionV2.USER_AGENT_OS_NAME]: ReportMetricV2.DIM_USER_AGENT_OS_NAME,
  [ReportDimensionV2.MEDIA_TYPE]: ReportMetricV2.DIM_MEDIA_TYPE,
  [ReportDimensionV2.COUNTRY]: ReportMetricV2.DIM_COUNTRY,
  [ReportDimensionV2.REGION]: ReportMetricV2.DIM_REGION,
  [ReportDimensionV2.APP_BUNDLE]: ReportMetricV2.DIM_APP_BUNDLE,
  [ReportDimensionV2.DEVICE_TYPE]: ReportMetricV2.DIM_DEVICE_TYPE,
  [ReportDimensionV2.VIEWABILITY_STANDARD]: ReportMetricV2.DIM_VIEWABILITY_STANDARD,
  [ReportDimensionV2.PROB_DECILE]: ReportMetricV2.DIM_PROB_DECILE,
  [ReportDimensionV2.GROUPM_VIEW_PROB_DECILE]: ReportMetricV2.DIM_GROUPM_VIEW_PROB_DECILE,
  [ReportDimensionV2.VIEW_PROB_DECILE]: ReportMetricV2.DIM_VIEW_PROB_DECILE,
  [ReportDimensionV2.PB_VERSION]: ReportMetricV2.DIM_PB_VERSION,
  [ReportDimensionV2.ENDPOINT]: ReportMetricV2.DIM_ENDPOINT,
  [ReportDimensionV2.EIDS_SOURCES]: ReportMetricV2.DIM_EIDS_SOURCES,
  [ReportDimensionV2.NOBID_REASON]: ReportMetricV2.DIM_NOBID_REASON,
  [ReportDimensionV2.US_PRIVACY]: ReportMetricV2.DIM_US_PRIVACY,
  [ReportDimensionV2.IVT_PROVIDER]: ReportMetricV2.DIM_IVT_PROVIDER,
  [ReportDimensionV2.IVT_TYPE]: ReportMetricV2.DIM_IVT_TYPE,
  [ReportDimensionV2.DSP_CREATIVE_ID]: ReportMetricV2.DIM_DSP_CREATIVE_ID
};

// Helper function to get the metric equivalent of a dimension
export const getDimensionAsMetric = (dimension: ReportDimensionV2): ReportMetricV2 | null => {
  return DimensionToMetricMap[dimension] || null;
};

// Helper function to check if a column name corresponds to a dimension
export const isDimensionColumn = (columnName: string): boolean => {
  return Object.values(ReportDimensionV2).includes(columnName as ReportDimensionV2);
};

// Helper function to get dimension from column name
export const getDimensionFromColumnName = (columnName: string): ReportDimensionV2 | null => {
  return Object.values(ReportDimensionV2).find(dim => dim === columnName) || null;
};

// Publisher-specific dimensions list (based on publisher report requirements)
export const PublisherDimensions = [
  // ReportDimensionV2.PUBLISHER_ID,
  ReportDimensionV2.PUBLISHER_AD_UNIT_ID,
  ReportDimensionV2.PUBLISHER_DOMAIN,
  ReportDimensionV2.INVENTORY_TYPE,
  ReportDimensionV2.CREATIVE_SIZE,
  ReportDimensionV2.PAGE_DOMAIN,
  ReportDimensionV2.DSP_ID,
  ReportDimensionV2.DSP_BUYER_ID,
  ReportDimensionV2.DEAL_ID,
  ReportDimensionV2.DP_ID,
  ReportDimensionV2.COUNTRY,
  ReportDimensionV2.REGION,
  ReportDimensionV2.EIDS_SOURCES,
  ReportDimensionV2.NOBID_REASON,
  // Time dimensions
  ReportDimensionV2.GRANULARITY_YEAR,
  ReportDimensionV2.GRANULARITY_QUARTER,
  ReportDimensionV2.GRANULARITY_MONTH,
  ReportDimensionV2.GRANULARITY_WEEK,
  ReportDimensionV2.GRANULARITY_DAY,
  ReportDimensionV2.GRANULARITY_HOUR,
  ReportDimensionV2.HOUR
];

export const TimeDimensions = [
  ReportDimensionV2.GRANULARITY_YEAR,
  ReportDimensionV2.GRANULARITY_QUARTER,
  ReportDimensionV2.GRANULARITY_MONTH,
  ReportDimensionV2.GRANULARITY_WEEK,
  ReportDimensionV2.GRANULARITY_DAY,
  ReportDimensionV2.GRANULARITY_HOUR,
  ReportDimensionV2.HOUR
];
